import React, { useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import { Ending } from '@customTypes/story';
import styled from '@emotion/styled';
import theme from '@styles/theme';
import { css } from '@emotion/react';
import { NextBtn, LinkBtn, LockIcon } from 'public/assets';
import { ENDING_STATS_PER_CHAPTER } from '@api/ending';
import { EndingStatsPerChapter } from '@customTypes/story';
import { ENDING_URL, FINAL_ENDING_URL } from '@common/values';
import {
  convertAllCharName,
  convertCharName,
  copyToClipboard,
  isAndroid,
} from '@common/utils';
import { Toast, ProgressBar } from '@components/commons';
import { isMobileDevice } from '@common/utils';
import { goPageForApp, onShareForApp } from '@/lib/webview';
import { shareEndingButton } from '@/lib/eventManager';

interface IStatistics {
  ending: Ending;
}

const Statistics = ({ ending }: IStatistics) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { chapterId } = router.query;
  const { data } = useQuery<EndingStatsPerChapter>(ENDING_STATS_PER_CHAPTER, {
    variables: {
      chapterId: Number(chapterId),
    },
    skip: chapterId === undefined || chapterId === null,
  });
  const [showToast, setShowToast] = useState<boolean>(false);
  const isStoryGame = router.pathname.includes('storygame');

  const handleStatOfThisChapter = () => {
    isMobileDevice()
      ? goPageForApp({
          link: `${window.location.origin}/${router.locale ?? 'ko'}/ending/${
            ending.chapterId
          }`,
        })
      : router.replace({
          pathname: `/ending/${ending.chapterId}`,
        });
  };

  const currentEnding = data?.endingData.endingWithStats.find(
    (item) => item.ending.endingId === ending.endingId,
  );
  const otherEndings = data?.endingData.endingWithStats.filter(
    (item) => item.ending.endingId !== ending.endingId,
  );
  const userHasEndingIds = data?.endingData.endingInfo.endings
    .filter((item) => item.userHasEnding)
    .map((item) => item.ending.endingId);

  if (!data) {
    return null;
  }

  const onLink = () => {
    const isFinal = currentEnding?.ending.isFinal;
    const endingURL = isFinal
      ? `${FINAL_ENDING_URL}/${currentEnding.ending.shareKey}`
      : `${ENDING_URL}/${currentEnding?.ending.shareKey}`;

    if (navigator.share) {
      navigator.share({
        title: currentEnding?.ending.name || '',
        url: endingURL,
      });
    } else {
      if (isAndroid()) {
        onShareForApp({
          link: endingURL,
          title: currentEnding?.ending.name || '',
        });
      } else {
        setShowToast(true);
        copyToClipboard(endingURL);
      }
    }

    const storyName = currentEnding?.ending.story?.name;
    const chapterIndex = currentEnding?.ending.chapter?.chapterIndex;
    const endingTitle = currentEnding?.ending.name;
    const storyId = currentEnding?.ending.story?.storyId;

    shareEndingButton({
      story: `${storyName}_${chapterIndex}`,
      endingTitle: endingTitle || '',
      button: 'LINK',
      storyId: storyId,
    });
  };

  return (
    <Container isStoryGame={isStoryGame}>
      <div className="title-container" onClick={handleStatOfThisChapter}>
        <div className="title">
          {t('chatstats_screen_title_statistics_flow')}
        </div>
        <NextBtn width={16} height={16} stroke={theme.colors.gray800} />
      </div>
      <div className="rate-container">
        <div className="rate-content">
          <div className="rate-name">
            {convertAllCharName({
              text:
                currentEnding?.ending?.displayName ||
                currentEnding?.ending?.name ||
                '',
              mainCharacterName: {
                firstName:
                  currentEnding?.ending.story?.lastPlay.firstName || '',
                lastName: currentEnding?.ending.story?.lastPlay.lastName,
              },
              otherCharacterNames:
                currentEnding?.ending.story?.characters?.map((item) => {
                  return {
                    name: item.name,
                    defaultFirstName: item.defaultFirstName,
                    defaultLastName: item.defaultLastName,
                    displayName: item.displayName,
                  };
                }) || [],
            })}
          </div>
          <div className="rate">{currentEnding?.percentage || 0}%</div>
        </div>
        <ProgressBar
          size={100}
          value={currentEnding?.percentage || 0}
          color={theme.colors.emerald500}
        />
      </div>
      {otherEndings?.map((endingInfo) => {
        const isUserHasEnding =
          userHasEndingIds?.indexOf(endingInfo.ending.endingId) === -1;

        if (isUserHasEnding) {
          return (
            <div key={endingInfo.ending.name} className="lock-container">
              <div
                className="lock-item"
                key={endingInfo.ending.displayName || endingInfo.ending.name}
              >
                <div className="lock-icon-wrapper">
                  <LockIcon width={16} height={20} fill={theme.colors.white} />
                </div>
              </div>
            </div>
          );
        } else {
          return (
            <div key={endingInfo.ending.name} className="rate-container">
              <div className="rate-content">
                <div className="rate-name">
                  {convertCharName(
                    endingInfo.ending.displayName || endingInfo.ending.name,
                    endingInfo.ending.story?.lastPlay.firstName || '',
                    endingInfo.ending.story?.lastPlay.lastName || '',
                  )}
                </div>
                <div className="rate other">{endingInfo.percentage || 0}%</div>
              </div>
              {endingInfo.percentage && (
                <ProgressBar
                  size={100}
                  color={theme.colors.gray400}
                  value={endingInfo.percentage}
                />
              )}
            </div>
          );
        }
      })}
      <div className="link-container">
        <div className="link" onClick={onLink}>
          <LinkBtn width={16} height={16} />
        </div>
      </div>
      {showToast && (
        <div className="toast-wrapper">
          <Toast
            message={t('common_toast_description_link_copied')}
            onClear={() => {
              setShowToast(false);
            }}
          />
        </div>
      )}
    </Container>
  );
};

const Container = styled.div<{ isStoryGame: boolean }>`
  margin: ${({ isStoryGame }) =>
    isStoryGame ? '0.75rem 1rem 0rem 1rem' : '1.25rem'};
  border: none;
  border-radius: 0.5em;
  background-color: ${theme.colors.white};
  display: flex;
  flex-direction: column;

  ${({ isStoryGame }) =>
    isStoryGame &&
    theme.setTabletStyle(css`
      margin: 0;
    `)}
  .title-container {
    padding: 1.25em 1.25em;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid ${theme.colors.gray300};
    cursor: pointer;

    .title {
      font-size: 1em;
      font-weight: 700;
      color: ${theme.colors.gray900};
    }
  }

  .rate-container {
    padding: 0.75em 1.25em;

    .rate-content {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin: 0.75em 0;

      .rate-name {
        font-size: 0.875em;
        font-weight: 400;
        color: ${theme.colors.gray900};
      }

      .rate {
        margin-top: 0.5em;
        font-size: 0.875em;
        color: ${theme.colors.emerald500};
        font-weight: 700;
      }

      .rate.other {
        color: ${theme.colors.gray600};
      }
    }
  }

  .link-container {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1em;
    margin: 0.75em 1.25em;

    .link {
      width: 2em;
      height: 2em;
      border-radius: 50%;
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: ${theme.colors.gray200};
      cursor: pointer;
    }

    .facebook {
      width: 2em;
      height: 2em;
      border-radius: 50%;
      display: flex;
      justify-content: center;
      background-color: #1877f2;
      cursor: pointer;
    }
  }

  .image-container {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .image-box {
    width: 100%;
    height: 3em;
    margin: 1.25em;
    background: ${theme.colors.gray100};
    border: 1px dashed ${theme.colors.gray300};
    box-sizing: border-box;
    border-radius: 0.25em;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .lock-container {
    margin: 0.75em 1.25em;
  }

  .lock-item {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 3em;
    border: 1px dashed ${theme.colors.gray300};
    border-radius: 0.25em;
    background: ${({ theme }) => theme.colors.gray100};

    .lock-icon-wrapper {
      position: relative;
      width: 1em;
      height: 1.25em;
      filter: brightness(80%);
    }
  }

  .toast-wrapper {
    position: fixed;
    display: flex;
    top: 0;
    left: 0;
    width: 100%;
    justify-content: center;
    align-items: center;
    bottom: 6em;
    z-index: 10;
  }
`;

export default Statistics;
